'use client';

import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface SettingsImportExportProps {
  onExport: () => Promise<string>;
  onImport: (data: string) => Promise<void>;
}

export function SettingsImportExport({ onExport, onImport }: SettingsImportExportProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importData, setImportData] = useState('');
  const [importError, setImportError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const data = await onExport();
      
      // Create and download file
      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vibecode-ai-settings-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleImport = async () => {
    setIsImporting(true);
    setImportError(null);
    
    try {
      await onImport(importData);
      setShowImportDialog(false);
      setImportData('');
    } catch (error) {
      setImportError(error instanceof Error ? error.message : 'Import failed');
    } finally {
      setIsImporting(false);
    }
  };

  const handleFileImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setImportData(content);
      setShowImportDialog(true);
    };
    reader.readAsText(file);
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <div className="bg-white/5 rounded-lg p-4">
        <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
          <span>💾</span>
          Settings Management
        </h3>

        <div className="flex gap-3">
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 disabled:bg-white/5 text-white/80 rounded transition-colors text-sm flex items-center justify-center gap-2"
          >
            {isExporting ? (
              <>
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <span>📤</span>
                Export Settings
              </>
            )}
          </button>

          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 text-white/80 rounded transition-colors text-sm flex items-center justify-center gap-2"
          >
            <span>📥</span>
            Import Settings
          </button>
          
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileImport}
            className="hidden"
          />
        </div>

        <p className="text-xs text-white/40 mt-3">
          Export your settings to backup or share configurations. API keys are not included for security.
        </p>
      </div>

      {/* Import Dialog */}
      <AnimatePresence>
        {showImportDialog && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
              onClick={() => setShowImportDialog(false)}
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-lg"
            >
              <div className="bg-[#0C0C0D] border border-white/20 rounded-lg shadow-2xl">
                <div className="p-6 border-b border-white/10">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <span>📥</span>
                    Import Settings
                  </h3>
                </div>

                <div className="p-6">
                  <div className="mb-4">
                    <label className="block text-xs text-white/70 mb-2">
                      Settings Data
                    </label>
                    <textarea
                      value={importData}
                      onChange={(e) => setImportData(e.target.value)}
                      placeholder="Paste your settings JSON here..."
                      rows={10}
                      className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-sm text-white font-mono focus:border-indigo-500 focus:outline-none resize-none"
                    />
                  </div>

                  {importError && (
                    <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded">
                      <p className="text-sm text-red-400">{importError}</p>
                    </div>
                  )}

                  <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded">
                    <p className="text-xs text-yellow-400">
                      <span className="font-medium">Note:</span> This will overwrite your current settings except for API keys, which must be re-entered for security.
                    </p>
                  </div>
                </div>

                <div className="p-6 border-t border-white/10 flex gap-3">
                  <button
                    onClick={() => {
                      setShowImportDialog(false);
                      setImportData('');
                      setImportError(null);
                    }}
                    className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 text-white/80 rounded transition-colors text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleImport}
                    disabled={isImporting || !importData}
                    className="flex-1 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 disabled:bg-indigo-500/50 text-white rounded transition-colors text-sm flex items-center justify-center gap-2"
                  >
                    {isImporting ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Importing...
                      </>
                    ) : (
                      'Import'
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}